import { useState } from 'react';

import useForm from '@prahatech/util-react-web/form/useForm';
import CustomStyleSheet from '@prahatech/util-react-web/stylesheet/CustomStyleSheet';
import is from '@prahatech/util-react-web/utils/is';

import { Button } from '../../../core/components';

export function FormSelector({ config, mode = 'view', isLoading, onSubmit, onToggleMode }) {
  const form = useForm(state => {
    form.showErrors();
    setFormState(state);
  });

  const [formState, setFormState] = useState(form.getState());

  const _toggleMode = () => {
    typeof onToggleMode === 'function' && onToggleMode(mode === 'view' ? 'input' : 'view');
  };

  const _submit = () => {
    const code = config.code;
    const practiceReference = formState.values.practiceReference;
    const email = formState.values.email;
    const contact = formState.values.contact;
    const appointment = formState.values.appointment;
    const amount = Number(
      /[\.,]/.test(formState.values.amount)
        ? formState.values.amount.replace(/[\.,]/, '')
        : formState.values.amount + '00'
    );

    const value = { code, practiceReference, email, contact, appointment, amount };

    typeof onSubmit === 'function' && onSubmit(value);
  };

  const _validatePracticeReference = value =>
    !value || value.length <= 50 || 'Practice reference has a maximum character limit of 50.';

  const _validateEmail = value =>
    /[A-Z]/g.test(value || '')
      ? 'Lower case required.'
      : is.email((value || '').toLowerCase()) || 'This must be a valid email address.';

  const _validateContact = value => {
    if (!value || (!value.code && !value.number)) return true;

    const { code, number } = value;

    return !code && number
      ? 'A country code is required.'
      : code && !number
      ? 'A number is required.'
      : !/^\d{1,7}$/.test(code) || !/^\d{4,12}$/.test(number)
      ? 'This must be a valid phone number.'
      : true;
  };

  const _validateAppointment = value => {
    const time = new Date(value).getTime();

    const today = new Date();
    today.setHours(0);
    today.setMinutes(0);
    today.setSeconds(0);
    today.setMilliseconds(0);

    return is.nan(time) ? 'This must be a valid date.' : time >= today.getTime() || 'The date must be in the future.';
  };

  const _validateAmount = value => {
    const required = config.payment.required;

    if (!required && !value) return true;

    if (value && !/^(\d*|\d*[\.,]\d{2})$/.test(value))
      return 'This value must be in a valid format (example: 3000.00 or 3000).';

    const valueCents = Number(/[\.,]/.test(value) ? value.replace(/[\.,]/, '') : value + '00');
    const exact = config.payment.amount.exact;

    if (exact) {
      const cents = String(exact);

      let rands = cents.replace(/^(\d*?)(\d{0,2})$/, '$1.$2');
      rands = rands.startsWith('.') ? '0' + rands : rands;

      return valueCents === exact || `The amount needs to be exactly R ${rands}`;
    }

    const min = config.payment.amount.min;
    const max = config.payment.amount.max;

    if (min && valueCents < min) {
      const cents = String(min);

      let rands = cents.replace(/^(\d*?)(\d{0,2})$/, '$1.$2');
      rands = rands.startsWith('.') ? '0' + rands : rands;

      return `The minimum charge amount is R ${rands}`;
    }

    if (max && valueCents > max) {
      const cents = String(max);

      let rands = cents.replace(/^(\d*?)(\d{0,2})$/, '$1.$2');
      rands = rands.startsWith('.') ? '0' + rands : rands;

      return `The maximum charge amount is R ${rands}`;
    }

    return true;
  };

  const Field = form.Field;

  const label = config.label;
  const hasValues = Object.keys(formState.values || {}).length;

  return (
    <div className='form-selector'>
      <div className='form-selector__left'>
        <div className='form-selector__icon' onClick={_toggleMode}>
          <svg viewBox='0 0 81 69' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M80.3114 9.79339L72.4026 0.925998C71.9213 0.396782 71.2573 0.0694163 70.5442 0.00983735C69.8311 -0.0497416 69.122 0.162878 68.5594 0.604882L54.3854 11.9547H7.13028C5.24136 11.9547 3.42961 12.7038 2.09278 14.0375C0.755945 15.3712 0.00327371 17.1806 0 19.0684V61.8739C0 62.8097 0.184441 63.7364 0.542771 64.601C0.901102 65.4656 1.42631 66.2511 2.08842 66.9128C2.75053 67.5745 3.53656 68.0995 4.40164 68.4576C5.26673 68.8157 6.19392 69 7.13028 69H58.6116C60.5006 68.9967 62.311 68.2445 63.6455 66.9084C64.98 65.5724 65.7296 63.7617 65.7296 61.8739V24.8235L80.0395 13.4984C80.31 13.2897 80.5342 13.0272 80.698 12.7275C80.8617 12.4277 80.9615 12.0973 80.9911 11.7571C81.0209 11.4005 80.9758 11.0417 80.8588 10.7035C80.7417 10.3654 80.5553 10.0554 80.3114 9.79339ZM62.5413 61.8863C62.5381 62.9203 62.1256 63.9111 61.394 64.6423C60.6624 65.3735 59.671 65.7857 58.6363 65.789H7.13028C6.09562 65.7857 5.10428 65.3735 4.37266 64.6423C3.64104 63.9111 3.22857 62.9203 3.22531 61.8863V19.0684C3.22857 18.0343 3.64104 17.0435 4.37266 16.3123C5.10428 15.5812 6.09562 15.1689 7.13028 15.1657H50.3815L28.3852 32.777L22.4907 47.3625C22.3942 47.5632 22.3603 47.7883 22.3934 48.0085C22.4264 48.2287 22.5249 48.4339 22.676 48.5975C22.7904 48.7277 22.931 48.8324 23.0885 48.9048C23.2461 48.9771 23.4171 49.0155 23.5905 49.0175H23.8747L39.8159 45.6088L62.5413 27.4417V61.911V61.8863ZM27.2359 45.5471C27.1333 45.5706 27.0262 45.5658 26.926 45.5331C26.8259 45.5005 26.7365 45.4413 26.6675 45.3618C26.6061 45.2921 26.5645 45.2072 26.5471 45.116C26.5297 45.0247 26.5371 44.9305 26.5686 44.8431L29.3985 37.2231L35.5773 44.1515L27.2359 45.5471ZM77.012 11.3742C76.9892 11.6287 76.9151 11.8759 76.794 12.1009C76.6729 12.3259 76.5074 12.5241 76.3076 12.6834L40.0012 41.7679L32.4261 33.2587L68.6583 4.2111C69.0985 3.86825 69.6517 3.70335 70.2079 3.7491C70.7642 3.79486 71.2829 4.04794 71.6612 4.45812L76.5176 9.87985C76.6977 10.0795 76.8346 10.3142 76.9197 10.5693C77.0047 10.8243 77.0362 11.0942 77.012 11.3619V11.3742Z'
              fill='#DEDEDE'
            />
          </svg>
        </div>
      </div>

      <div className='form-selector__right'>
        <div className='form-selector__label' onClick={_toggleMode}>
          {label}
        </div>

        {mode === 'input' && (
          <div className='form-selector__input'>
            <Field
              type='text'
              name='practiceReference'
              label='Reference (optional)'
              validate={_validatePracticeReference}
            />

            <Field type='text' name='email' label='Email address (lower case)' validate={_validateEmail} />

            <Field type='contact' name='contact' label='Phone number (optional)' validate={_validateContact} />

            <Field type='date' name='appointment' label='Appointment Date' validate={_validateAppointment} />

            {!(config.payment && config.payment.enabled) || (
              <Field type='text' name='amount' label='Charge Amount (R)' validate={_validateAmount} />
            )}

            <div className='form-selector__actions'>
              <div className='form-selector__action'>
                <Button label='Send' onPress={_submit} disabled={!hasValues || !formState.valid} loading={isLoading} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const scope = '.form-selector';
const styles = `
  {
    width: 100%;
    display: flex;
    flex-direction: row;
    box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.1);
    border-radius: 7px;
    padding: 1ch;
    margin-bottom: 3ch;
  }

  .form-selector__left {
    width: 80px;
  }

  .form-selector__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    cursor: pointer;
  }

  .form-selector__icon svg {
    height: 50px;
    width: 50px;
  }

  .form-selector__right {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0 .5em;
  }

  .form-selector__label {
    display: flex;
    align-items: center;
    height: 80px;
    color: conf(color, primary);
    font-preset: 1.2em light;
    cursor: pointer;
    user-select: none;
  }

  .form-selector__actions {
    display: flex;
    justify-content: flex-start;
  }

  .form-selector__action {
    min-width: 90px;
  }
`;

CustomStyleSheet.create(styles, scope);
